/**
 * DEX Screener adapter (https://dexscreener.com), public and keyless. Raw
 * pair shapes stop here; the app reads `DexPair`.
 */
import { fetchWithRetry, UpstreamError } from './fetchWrapper';

const DEX_BASE = 'https://api.dexscreener.com';
/** DEX Screener accepts up to 30 token addresses per call. */
export const DEX_MAX_ADDRESSES = 30;

export interface DexPair {
  chainId: string;
  dexId: string;
  pairAddress: string;
  /** The token the pair prices (base side). */
  baseAddress: string;
  baseName: string | null;
  baseSymbol: string | null;
  quoteSymbol: string | null;
  priceUsd: number | null;
  liquidityUsd: number | null;
  fdv: number | null;
  volume24h: number | null;
  /** Pair creation time, ms; null when unknown. */
  createdMs: number | null;
}

type Raw = Record<string, unknown>;
const isObj = (x: unknown): x is Raw => typeof x === 'object' && x !== null;

function num(v: unknown): number | null {
  if (v === '' || v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function text(v: unknown, max: number): string | null {
  if (typeof v !== 'string') return null;
  // eslint-disable-next-line no-control-regex
  const clean = v.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  return clean ? clean.slice(0, max) : null;
}

export function parsePair(p: unknown): DexPair | null {
  if (!isObj(p)) return null;
  const base = isObj(p.baseToken) ? p.baseToken : null;
  const quote = isObj(p.quoteToken) ? p.quoteToken : null;
  if (
    typeof p.chainId !== 'string' ||
    typeof p.pairAddress !== 'string' ||
    !base ||
    typeof base.address !== 'string'
  ) {
    return null;
  }
  const liquidity = isObj(p.liquidity) ? p.liquidity : {};
  const volume = isObj(p.volume) ? p.volume : {};
  return {
    chainId: p.chainId,
    dexId: typeof p.dexId === 'string' ? p.dexId : '',
    pairAddress: p.pairAddress,
    baseAddress: base.address,
    baseName: text(base.name, 40),
    baseSymbol: text(base.symbol, 16),
    quoteSymbol: text(quote?.symbol, 16),
    priceUsd: num(p.priceUsd),
    liquidityUsd: num(liquidity.usd),
    fdv: num(p.fdv),
    volume24h: num(volume.h24),
    createdMs: num(p.pairCreatedAt),
  };
}

/**
 * Every pair DEX Screener tracks for these tokens on one chain. Includes
 * pairs where the token is the quote side; callers filter by base address.
 */
export async function fetchDexPairs(
  chainId: string,
  addresses: readonly string[],
): Promise<DexPair[]> {
  if (addresses.length === 0) return [];
  if (addresses.length > DEX_MAX_ADDRESSES) {
    throw new Error(`fetchDexPairs takes at most ${DEX_MAX_ADDRESSES} addresses.`);
  }
  const url = `${DEX_BASE}/tokens/v1/${encodeURIComponent(chainId)}/${addresses
    .map(encodeURIComponent)
    .join(',')}`;
  const response = await fetchWithRetry(url, { timeoutMs: 10_000 });
  if (!response.ok) {
    throw new UpstreamError(
      `DEX Screener lookup failed with status ${response.status}.`,
      502,
      'dexscreener',
    );
  }
  const body: unknown = await response.json();
  // The tokens endpoint returns an array; older endpoints wrap it in { pairs }.
  const rows = Array.isArray(body)
    ? body
    : isObj(body) && Array.isArray(body.pairs)
      ? body.pairs
      : null;
  if (rows === null) {
    throw new UpstreamError(
      'DEX Screener returned a malformed body.',
      502,
      'dexscreener',
    );
  }
  return rows.map(parsePair).filter((p): p is DexPair => p !== null);
}

/** DEX Screener's page for a pair, built from validated parts. */
export function dexPairUrl(chainId: string, pairAddress: string): string | null {
  if (!/^[a-z0-9-]{2,30}$/.test(chainId)) return null;
  if (!/^[A-Za-z0-9]{20,90}$/.test(pairAddress)) return null;
  return `https://dexscreener.com/${chainId}/${pairAddress}`;
}
